import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import io, { Socket } from 'socket.io-client';

// Components
import HeroPage from './components/HeroPage';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AssetViewer from './components/AssetViewer';
import ComponentDetail from './components/ComponentDetail';
import ReportGenerator from './components/ReportGenerator';
import AIModelBuilder from './components/AIModelBuilder';
import AssetManagementDashboard from './components/AssetManagementDashboard';
import Navbar from './components/Navbar';

// Services
import { AuthService } from './services/AuthService';
import { SocketService } from './services/SocketService';

// Types
import { User, Organization, Asset, Component } from './types';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00D4FF',
    },
    secondary: {
      main: '#34C759',
    },
    background: {
      default: '#000000',
      paper: '#111111',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 200,
      letterSpacing: '-0.05em',
    },
    h2: {
      fontWeight: 200,
      letterSpacing: '-0.03em',
    },
    h3: {
      fontWeight: 300,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 300,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 400,
    },
    h6: {
      fontWeight: 400,
    },
    body1: {
      fontWeight: 300,
      lineHeight: 1.7,
    },
    body2: {
      fontWeight: 300,
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          textTransform: 'none',
          fontWeight: 300,
          letterSpacing: '0.03em',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#111111',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [loading, setLoading] = useState(true);
  const [showHero, setShowHero] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      AuthService.getProfile()
        .then((userData) => {
          setUser(userData);
          initializeSocket();
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const initializeSocket = React.useCallback(() => {
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
      auth: {
        token: localStorage.getItem('token')
      }
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
      if (user?.organizationId) {
        newSocket.emit('join_organization', user.organizationId);
      }
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
    });

    newSocket.on('health_update', (data) => {
      console.log('Health update received:', data);
      // Handle real-time health updates
    });

    newSocket.on('component_sync', (data) => {
      console.log('Component sync received:', data);
      // Handle real-time component synchronization
    });

    newSocket.on('alert', (data) => {
      console.log('Alert received:', data);
      // Handle real-time alerts
    });

    setSocket(newSocket);
    SocketService.setSocket(newSocket);
    setLoading(false);
  }, [user]);

  const handleGetStarted = () => {
    setShowHero(false);
  };

  const handleLogin = (userData: User) => {
    setUser(userData);
    if (!socket) {
      initializeSocket();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        Loading...
      </Box>
    );
  }

  // Show hero page if user is not logged in and hero should be shown
  if (!user && showHero) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HeroPage onGetStarted={handleGetStarted} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {user && <Navbar user={user} onLogout={handleLogout} />}
          
          <Routes>
            <Route
              path="/login"
              element={
                user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />
              }
            />
            <Route
              path="/dashboard"
              element={
                user ? <Dashboard user={user} socket={socket} /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/asset/:assetId"
              element={
                user ? <AssetViewer user={user} socket={socket} /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/component/:componentId"
              element={
                user ? <ComponentDetail user={user} socket={socket} /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/reports"
              element={
                user ? <ReportGenerator user={user} /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/ai-models"
              element={
                user ? <AIModelBuilder user={user} /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/assets"
              element={
                user ? <AssetManagementDashboard user={user} /> : <Navigate to="/login" />
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;